import {
    getGoogleIntegrations,
    getGoogleIntegrationForActiveSchool,
    linkSchoolGoogleIntegration,
    unlinkSchoolGoogleIntegration,
    uploadToLinkedSchoolGoogleDrive,
} from "./dataService";

const GOOGLE_DRIVE_CONFIG = {
    CLIENT_ID: process.env.REACT_APP_GOOGLE_CLIENT_ID || "your-client-id",
};

let gisCodeClientPromise = null;

const formatDateLocal = (dateString) => {
    const localDate = new Date(dateString);
    return (
        localDate.getFullYear() +
        "-" +
        String(localDate.getMonth() + 1).padStart(2, "0") +
        "-" +
        String(localDate.getDate()).padStart(2, "0")
    );
};

const ensureGisScriptLoaded = async () => {
    if (window.google?.accounts?.oauth2) {
        return;
    }
    if (gisCodeClientPromise) {
        return gisCodeClientPromise;
    }

    gisCodeClientPromise = new Promise((resolve, reject) => {
        const existing = document.querySelector("script[data-google-gsi='true']");
        if (existing) {
            existing.addEventListener("load", () => resolve(), { once: true });
            existing.addEventListener(
                "error",
                () => reject(new Error("Google Identity Services script load failed")),
                { once: true }
            );
            return;
        }

        const script = document.createElement("script");
        script.src = "https://accounts.google.com/gsi/client";
        script.async = true;
        script.defer = true;
        script.dataset.googleGsi = "true";
        script.onload = () => resolve();
        script.onerror = () =>
            reject(new Error("Google Identity Services script load failed"));
        document.head.appendChild(script);
    });

    return gisCodeClientPromise;
};

const requestOAuthCode = async () => {
    if (!GOOGLE_DRIVE_CONFIG.CLIENT_ID || GOOGLE_DRIVE_CONFIG.CLIENT_ID === "your-client-id") {
        throw new Error("REACT_APP_GOOGLE_CLIENT_ID が設定されていません");
    }

    await ensureGisScriptLoaded();

    if (!window.google?.accounts?.oauth2) {
        throw new Error("Google Identity Services is not available");
    }

    return new Promise((resolve, reject) => {
        const codeClient = window.google.accounts.oauth2.initCodeClient({
            client_id: GOOGLE_DRIVE_CONFIG.CLIENT_ID,
            scope:
                "https://www.googleapis.com/auth/drive.file https://www.googleapis.com/auth/userinfo.email",
            ux_mode: "popup",
            callback: (response) => {
                if (response.error) {
                    reject(new Error(response.error));
                    return;
                }
                if (!response.code) {
                    reject(new Error("authorization code not returned"));
                    return;
                }
                resolve(response.code);
            },
        });

        codeClient.requestCode({
            prompt: "consent",
        });
    });
};

const blobToDataUrl = (blob) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error("failed to convert blob to dataUrl"));
        reader.readAsDataURL(blob);
    });

export const getSchoolGoogleIntegrations = async () => {
    return await getGoogleIntegrations();
};

export const getActiveSchoolGoogleIntegration = async () => {
    return await getGoogleIntegrationForActiveSchool();
};

export const connectSchoolGoogleIntegration = async (schoolId) => {
    const code = await requestOAuthCode();
    return await linkSchoolGoogleIntegration(schoolId, code);
};

export const disconnectSchoolGoogleIntegration = async (schoolId) => {
    return await unlinkSchoolGoogleIntegration(schoolId);
};

export const uploadEvaluationAndResultImagesViaSchoolIntegration = async (
    evaluationBlob,
    imageFiles,
    imageData,
    classRecord
) => {
    const classDate = formatDateLocal(classRecord.date);
    const evaluationDataUrl = await blobToDataUrl(evaluationBlob);
    const resultImagesPayload = [];

    for (let i = 0; i < (imageFiles || []).length; i += 1) {
        const blob = imageFiles[i];
        const meta = imageData?.[i];
        const dataUrl = await blobToDataUrl(blob);
        const safeName = meta?.name || `result_${i + 1}.png`;
        const fileName = `成果物_${classRecord.studentName}_${classDate}_${i + 1}_${safeName}`;
        resultImagesPayload.push({
            fileName,
            dataUrl,
        });
    }

    return await uploadToLinkedSchoolGoogleDrive({
        classRecord: {
            studentName: classRecord.studentName,
            date: classRecord.date,
        },
        evaluationImage: {
            fileName: `評価シート_${classRecord.studentName}_${classDate}.png`,
            dataUrl: evaluationDataUrl,
        },
        resultImages: resultImagesPayload,
    });
};
